import {
  type SecurityPerforation,
  securityHoleUsesPoints,
} from '@briefmarken_katalog/types';
import { translate, type TranslationKey, type Locale } from '$lib/i18n';

/**
 * Rendering of a security perforation for read-only contexts — the catalog fact list, the
 * chip in the item row and the summary on the editor's button all go through here so the
 * three never drift apart.
 */

function num(value: number, loc: Locale): string {
  return new Intl.NumberFormat(loc).format(value);
}

/** The shape, with the free text taking over for a motif ("Herz" instead of "Motivform"). */
export function securityHoleShapeLabel(security: SecurityPerforation): string {
  if (security.shape === 'Motif' && security.shapeText?.trim()) return security.shapeText.trim();
  return translate(`fields.securityPerforation.shapes.${security.shape}` as TranslationKey);
}

/** The measurement alone, in mm — empty when none was recorded. */
export function formatSecurityHoleDimensions(security: SecurityPerforation, loc: Locale): string {
  const d = security.dimensions;
  if (!d) return '';
  const mm = translate('fields.mm');
  switch (d.kind) {
    case 'Round':
      return `⌀ ${num(d.diameter, loc)} ${mm}`;
    case 'Elliptic':
      return `${num(d.majorAxis, loc)} × ${num(d.minorAxis, loc)} ${mm}`;
    case 'Polygonal': {
      const radii = `${translate('fields.securityPerforation.circumradiusShort')} ${num(d.circumradius, loc)} / `
        + `${translate('fields.securityPerforation.inradiusShort')} ${num(d.inradius, loc)} ${mm}`;
      return securityHoleUsesPoints(security.shape) && d.points !== undefined
        ? `${radii}, ${d.points} ${translate('fields.securityPerforation.pointsShort')}`
        : radii;
    }
  }
}

/** One line: shape, count per side, affected sides, position, orientation, measurement. */
export function formatSecurityPerforation(security: SecurityPerforation, loc: Locale): string {
  const parts: string[] = [securityHoleShapeLabel(security)];

  parts.push(`${num(security.holesPerSide, loc)}×/${translate('fields.securityPerforation.perSideShort')}`);

  // The inner separation narrows the chosen sides rather than replacing them, so it reads
  // as a qualifier: "alle vier Seiten (nur Innentrennung)".
  const scope = translate(`fields.securityPerforation.scopes.${security.scope}` as TranslationKey);
  parts.push(security.innerSeparationOnly
    ? `${scope} (${translate('fields.securityPerforation.innerSeparationOnlyShort')})`
    : scope);

  parts.push(
    security.position === 'Nth' && security.positionIndex !== undefined
      ? translate('fields.securityPerforation.positions.Nth').replace('{n}', String(security.positionIndex))
      : translate('fields.securityPerforation.positions.Center')
  );
  if (security.orientation) {
    parts.push(translate(`fields.securityPerforation.orientations.${security.orientation}` as TranslationKey));
  }
  const dimensions = formatSecurityHoleDimensions(security, loc);
  if (dimensions) parts.push(dimensions);

  return parts.join(', ');
}

/** Short form for the chip in an item row, where a full line does not fit. */
export function formatSecurityPerforationShort(security: SecurityPerforation): string {
  return `${translate('fields.securityPerforation.short')}: ${securityHoleShapeLabel(security)}`;
}
